import React, { useState, useEffect, useCallback, useRef } from "react";
import createPersistedState from "use-persisted-state";
import {FOOTER_HEIGHT, HEADER_HEIGHT, SIDEBAR_WIDTH} from "../conf/SMCConsts";
import { useSnackbar } from "notistack";
import SMCButton from "../components/Simple/SMCButton";
import store from "../store";
import { push } from "connected-react-router";
import { SMCPaths } from "../conf/Routes";
import { useSelector } from "react-redux";
import { showSidebarSelector} from "../store/selectors/view.selectors";
import { SortDirections } from "../api/consts";

/**
 * Use a boolean value
 * @param {boolean} initialValue
 * @returns {[boolean, function, function]} [value, setTrue, setFalse]
 */
export function useBoolean(initialValue) {
  const [value, setValue] = useState(initialValue);

  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);

  return [value, setTrue, setFalse];
}

/**
 * Uses a toggled value
 * @param {boolean} initialValue
 * @returns {[boolean, function]} [value, toggleValue]
 */
export function useToggle(initialValue) {
  const [value, setValue] = useState(initialValue);
  const toggleValue = useCallback(() => setValue((val) => !val), []);

  return [value, toggleValue];
}

/**
 * Call a function every X seconds
 * @param {Function} callback Function to call every delay seconds
 * @param {Number} delay Seconds to wait between each callback call
 */
export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the last callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// Ref that holds a value of true if the component is mounted, and false if unmounted
export function useIsMountedRef() {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  return isMountedRef;
}

export function useSort(defaultSortBasis, defaultSortDirection) {
  const [sortBasis, setSortBasis] = useState(defaultSortBasis);
  const [sortDirection, setSortDirection] = useState(
    defaultSortDirection || SortDirections.ASCENDING
  );

  const setSort = useCallback(
    (newSortBasis) => {
      // If new sort basis is same as current sort basis, toggle sort direction
      if (sortBasis === newSortBasis) {
        setSortDirection((sd) =>
          sd === SortDirections.ASCENDING
            ? SortDirections.DESCENDING
            : SortDirections.ASCENDING
        );
      } else {
        setSortBasis(newSortBasis);
        setSortDirection(SortDirections.ASCENDING);
      }
    },
    [sortBasis]
  );

  return { sortBasis, sortDirection, setSort };
}

export function useSMCSnackbar(closeAllOnPageExit = true) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const successAction = useCallback(
    (key) => (
      <SMCButton
        onClick={() => closeSnackbar(key)}
        style={{
          background: "none",
          border: "none",
          padding: "4px 8px",
          minWidth: "64px",
        }}
      >
        <span style={{ color: "#fff", fontWeight: "500" }}>Close</span>
      </SMCButton>
    ),
    [closeSnackbar]
  );

  const errorAction = useCallback(
    (key) => (
      <SMCButton
        onClick={() => closeSnackbar(key)}
        style={{
          background: "none",
          border: "none",
          padding: "4px 8px",
          minWidth: "64px",
        }}
      >
        <span style={{ color: "#fff", fontWeight: "500" }}>Close</span>
      </SMCButton>
    ),
    [closeSnackbar]
  );

  const logoutAction = useCallback(
    (key) => (
      <SMCButton
        onClick={() => {
          store.dispatch(push(SMCPaths.logout));
          closeSnackbar(key);
        }}
        style={{
          background: "none",
          border: "none",
          padding: "4px 8px",
          minWidth: "64px",
        }}
      >
        <span style={{ color: "#fff", fontWeight: "500" }}>Logout</span>
      </SMCButton>
    ),
    [closeSnackbar]
  );

  const enqueueErrorSnackbar = useCallback(
    (message, options) => {
      enqueueSnackbar(message, {
        variant: "error",
        autoHideDuration: 6000,
        action: errorAction,
        ...options,
      });
    },
    [enqueueSnackbar, errorAction]
  );

  const enqueueLogoutSnackbar = useCallback(
    (message, options) => {
      enqueueSnackbar(message, {
        variant: "error",
        persist: true,
        action: logoutAction,
        ...options,
      });
    },
    [enqueueSnackbar, logoutAction]
  );

  const enqueueSuccessSnackbar = useCallback(
    (message, options) => {
      enqueueSnackbar(message, {
        variant: "success",
        action: successAction,
        ...options,
      });
    },
    [enqueueSnackbar, successAction]
  );

  // useEffect(() => {
  //     return () => {
  //         if (closeAllOnPageExit) {
  //             closeSnackbar();
  //         }
  //     }
  // }, [closeAllOnPageExit, closeSnackbar]);

  return {
    enqueueSnackbar,
    enqueueErrorSnackbar,
    enqueueLogoutSnackbar,
    enqueueSuccessSnackbar,
    closeSnackbar,
  };
}

/**
 * Follow window dimensions, returns window width and window height,
 * and updates them on every window resize
 */
export function useWindowDimensions() {
  const getWindowWidth = () => {
    return window.innerWidth;
  };
  const getWindowHeight = () => {
    return window.innerHeight;
  };

  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const [windowHeight, setWindowHeight] = useState(getWindowHeight());

  // Add resize listener to update window size
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowWidth(getWindowWidth());
      setWindowHeight(getWindowHeight());
    };

    window.addEventListener("resize", updateWindowSize);
    return () => {
      window.removeEventListener("resize", updateWindowSize);
    };
  }, []);

  return {
    windowWidth,
    windowHeight,
  };
}

export function useResultsTableDimensions() {
    const showSidebar = useSelector(showSidebarSelector);
    
    const getTableWidth = useCallback(() => {
        let width = parseInt((window.innerWidth || document.body['clientWidth'] ) * 0.97);
        if (showSidebar) {
            width -= SIDEBAR_WIDTH;
        }
        return width;
    }, [showSidebar]);

    const getTableHeight = useCallback(() => {
        return window.innerHeight - 130 - FOOTER_HEIGHT - HEADER_HEIGHT;
    }, []);

  const [tableWidth, setTableWidth] = useState(getTableWidth());
  const [tableHeight, setTableHeight] = useState(getTableHeight());

  // Add resize listener to update table size
  useEffect(() => {
    const updateTableSize = () => {
      setTableWidth(getTableWidth());
      setTableHeight(getTableHeight());
    };

    window.addEventListener("resize", updateTableSize);
    return () => {
      window.removeEventListener("resize", updateTableSize);
    };
  }, [getTableWidth, getTableHeight]);

  useEffect(() => {
    setTableWidth(getTableWidth());
    setTableHeight(getTableHeight());
  }, [getTableWidth, getTableHeight, showSidebar]);

  return [tableWidth, tableHeight];
}

export function useResultsTableDropdownPosition() {
  const getDropdownLeft = () => {
    return window.innerWidth * 0.97 - SIDEBAR_WIDTH + 60;
  };

  const [dropdownLeft, setDropdownLeft] = useState(getDropdownLeft());

  // Add resize listener to update dropdown position
  useEffect(() => {
    const updateDropdownPositon = () => {
      setDropdownLeft(getDropdownLeft());
    };

    window.addEventListener("resize", updateDropdownPositon);
    return () => {
      window.removeEventListener("resize", updateDropdownPositon);
    };
  }, []);

  return [dropdownLeft];
}

/**
 * Dynamic modal dimensions
 * @param {Object} params
 * @param {Number} params.maxWidth
 * @param {Number} params.maxHeight
 * @returns {{modalWidth: number, modalHeight: number}}
 */
export function useModalDimensions({ maxWidth, maxHeight }) {
  const getModalWidth = useCallback(() => {
    const maxModalWidth = window.innerWidth - 10;
    return Math.min(maxModalWidth, maxWidth);
  }, [maxWidth]);
  const getModalHeight = useCallback(() => {
    const maxModalHeight = window.innerHeight - 10;
    return Math.min(maxModalHeight, maxHeight);
  }, [maxHeight]);

  const [modalWidth, setModalWidth] = useState(getModalWidth());
  const [modalHeight, setModalHeight] = useState(getModalHeight());

  // Add resize listener to update modal size
  useEffect(() => {
    const updateModalSize = () => {
      setModalWidth(getModalWidth());
      setModalHeight(getModalHeight());
    };

    window.addEventListener("resize", updateModalSize);
    return () => {
      window.removeEventListener("resize", updateModalSize);
    };
  }, [getModalHeight, getModalWidth]);

  return {
    modalWidth,
    modalHeight,
  };
}

/**
 * Dynamic map dimensions
 * @returns {{mapWidth: number, mapHeight: number}}
 */
export function useMapDimensions() {
  const showSidebar = useSelector(showSidebarSelector);

  const getMapWidth = useCallback(() => {
    let width =
      parseInt(window.innerWidth || document.body["clientWidth"]) - 50;
    if (showSidebar) {
      width -= SIDEBAR_WIDTH;
    }
    return width < 100 ? 100 : width; // minimum width of 100
  }, [showSidebar]);

    const getMapHeight = useCallback(() => {
        const height = window.innerHeight - 80 - HEADER_HEIGHT - FOOTER_HEIGHT;
        return height < 100 ? 100 : height; // minimum height of 100
    }, []);

  const [mapWidth, setMapWidth] = useState(getMapWidth());
  const [mapHeight, setMapHeight] = useState(getMapHeight());

  // Add resize listener to update map size
  useEffect(() => {
    const updateEditorSize = () => {
      setMapWidth(getMapWidth());
      setMapHeight(getMapHeight());
    };

    window.addEventListener("resize", updateEditorSize);
    return () => {
      window.removeEventListener("resize", updateEditorSize);
    };
  }, [getMapWidth, getMapHeight]);

  useEffect(() => {
    setMapWidth(getMapWidth());
    setMapHeight(getMapHeight());
  }, [getMapWidth, getMapHeight, showSidebar]);

  return {
    mapWidth,
    mapHeight,
  };
}

/**
 * Dynamic editor dimensions
 * @returns {{editorWidth: number, editorHeight: number}}
 */
export function useEditorDimensions(withTabs) {
    const showSidebar = useSelector(showSidebarSelector);
    
    const getEditorWidth = useCallback(() => {
        let width = parseInt((window.innerWidth || document.body['clientWidth'])) - 56;
        if (showSidebar) {
            width -= SIDEBAR_WIDTH;
        }
        return width < 780 ? 780 : width; // minimum width of 780
    }, [showSidebar]);

    const getEditorHeight = useCallback(() => {
        const height = window.innerHeight - 120 - HEADER_HEIGHT - FOOTER_HEIGHT - (withTabs ? 20 : 0);
        return height < 100 ? 100 : height; // minimum height of 100
    }, [withTabs]);

    const [editorWidth, setEditorWidth] = useState(getEditorWidth());
    const [editorHeight, setEditorHeight] = useState(getEditorHeight());


    // Add resize listener to update editor size
    useEffect(() => {
        const updateEditorSize = () => {
            setEditorWidth(getEditorWidth());
            setEditorHeight(getEditorHeight());
        }

        window.addEventListener("resize", updateEditorSize);
        return () => {
            window.removeEventListener("resize", updateEditorSize);
        }
    }, [getEditorWidth, getEditorHeight]);

    // Update size on sidebar toggle
    useEffect(() => {
        setEditorWidth(getEditorWidth());
        setEditorHeight(getEditorHeight());
    }, [showSidebar, getEditorWidth, getEditorHeight]);
    
    return {
        editorWidth,
        editorHeight
    };
}

/**
 * Cyber views iframes dimensions
 * @returns {{cyberViewsWidth: number, cyberViewsHeight: number}}
 */
export function useCyberViewsDimensions() {
  const showSidebar = useSelector(showSidebarSelector);

  const getCyberViewsWidth = useCallback(() => {
    let width = parseInt(
      (window.innerWidth || document.body["clientWidth"]) * 0.97
    );
    if (showSidebar) {
      width -= SIDEBAR_WIDTH;
    }
    return width;
  }, [showSidebar]);

  const getCyberViewsHeight = useCallback(() => {
    return window.innerHeight - 50 - FOOTER_HEIGHT - HEADER_HEIGHT;
  }, []);

  // Update sizes on sidebar toggle
  useEffect(() => {
    setCyberViewsWidth(getCyberViewsWidth());
    setCyberViewsHeight(getCyberViewsHeight());
  }, [showSidebar, getCyberViewsWidth, getCyberViewsHeight]);

  const [cyberViewsWidth, setCyberViewsWidth] = useState(getCyberViewsWidth());
  const [cyberViewsHeight, setCyberViewsHeight] = useState(
    getCyberViewsHeight()
  );

  // Add resize listener to update CyberViews size
  useEffect(() => {
    const updateCyberViewsSize = () => {
      setCyberViewsWidth(getCyberViewsWidth());
      setCyberViewsHeight(getCyberViewsHeight());
    };

    window.addEventListener("resize", updateCyberViewsSize);
    return () => {
      window.removeEventListener("resize", updateCyberViewsSize);
    };
  }, [getCyberViewsWidth, getCyberViewsHeight]);

  return [cyberViewsWidth, cyberViewsHeight];
}

export function useColumnsStatePersistedState(objectType, defaultColumnsState) {
  const didCheckColumns = useRef(false);
  const _useColumnsStatePersistedState = createPersistedState(
    "columns-state-" + objectType
  );
  const [columnsState, setColumnsState] =
    _useColumnsStatePersistedState(defaultColumnsState);

  // Check for missing columns in persisted state
  useEffect(() => {
    // Only run this once on mount
    if (didCheckColumns.current) {
      return;
    }

    let columnsStateCopy;
    // Iterate over all default columns state, and check are any columns that don't exist in the persisted columns state.
    // If there are, add them to the columns state.
    for (let i = 0; i < defaultColumnsState.length; i++) {
      const columnsStateHasColumn = columnsState.find(
        (col) => col.id === defaultColumnsState[i].id
      );
      if (!columnsStateHasColumn) {
        if (!columnsStateCopy) {
          columnsStateCopy = [...columnsState]; // Lazy init for performance
        }
        columnsStateCopy.splice(i, 0, defaultColumnsState[i]); // Insert missing column
      }
    }
    if (columnsStateCopy) {
      setColumnsState(columnsStateCopy);
    }
    didCheckColumns.current = true;
  }, [columnsState, defaultColumnsState, setColumnsState]);

  return [columnsState, setColumnsState];
}

export function useColumnsWidthsPersistedState(objectType, defaultWidths) {
  const _useColumnsWidthsPersistedState = createPersistedState(
    "columns-widths-" + objectType
  );
  const [columnsWidths, setColumnsWidths] =
    _useColumnsWidthsPersistedState(defaultWidths);

  return [columnsWidths, setColumnsWidths];
}

export function useMapPersistedState() {
  const defaultMapState = {
    connected: true,
    disconnected: true,
    disabled: true,
    autoFit: true,
    refreshInterval: 0,
    zoom: 2,
    location: {
      lat: 23.20601139347756,
      lng: -17.607752370964967,
    },
  };

  const _useMapPersistedState = createPersistedState("googleMapsSettings");
  const [mapPersistedState, setMapPersistedState] =
    _useMapPersistedState(defaultMapState);

  const connected = mapPersistedState.connected;
  const disconnected = mapPersistedState.disconnected;
  const disabled = mapPersistedState.disabled;
  const autoFit = mapPersistedState.autoFit;
  const refreshInterval = mapPersistedState.refreshInterval;
  const zoom = mapPersistedState.zoom;
  const location = mapPersistedState.location;

  const setConnected = useCallback(
    (connected) => {
      setMapPersistedState((state) => ({ ...state, connected: connected }));
    },
    [setMapPersistedState]
  );

  const setDisconnected = useCallback(
    (disconnected) => {
      setMapPersistedState((state) => ({
        ...state,
        disconnected: disconnected,
      }));
    },
    [setMapPersistedState]
  );

  const setDisabled = useCallback(
    (disabled) => {
      setMapPersistedState((state) => ({ ...state, disabled: disabled }));
    },
    [setMapPersistedState]
  );

  const setAutoFit = useCallback(
    (autoFit) => {
      setMapPersistedState((state) => ({ ...state, autoFit: autoFit }));
    },
    [setMapPersistedState]
  );

  const setRefreshInterval = useCallback(
    (refreshInterval) => {
      setMapPersistedState((state) => ({
        ...state,
        refreshInterval: refreshInterval,
      }));
    },
    [setMapPersistedState]
  );

  const setZoom = useCallback(
    (zoom) => {
      setMapPersistedState((state) => ({ ...state, zoom: zoom }));
    },
    [setMapPersistedState]
  );

  const setLocation = useCallback(
    (location) => {
      setMapPersistedState((state) => ({ ...state, location: location }));
    },
    [setMapPersistedState]
  );

  return {
    connected,
    setConnected,
    disconnected,
    setDisconnected,
    disabled,
    setDisabled,
    autoFit,
    setAutoFit,
    refreshInterval,
    setRefreshInterval,
    zoom,
    setZoom,
    location,
    setLocation,
  };
}
